import { ref, Ref } from 'vue';
import { LOGIN_NOT_CONFIGURED_MESSAGE } from './authUtilities';
import { useAuthStatus } from './useAuthStatus';

export interface UseLoginResult {
	/**
	 * Sends the user to login if they are not logged in. The browser navigates away, and after login the CMS sends
	 * the user back to `returnUrl`, with `?login=cancelled` added if login was cancelled or failed.
	 *
	 * Resolves true when the user is already logged in (no redirect). Resolves false when the browser is being
	 * redirected, when login is not configured (see `message`), or when the status check failed (see `error`).
	 * Never rejects.
	 */
	login: (returnUrl?: string) => Promise<boolean>;
	/** Set when login cannot be started for this brand. */
	message: Ref<string | null>;
	/** Set when the status check or browser navigation failed. */
	error: Ref<string | null>;
	loading: Ref<boolean>;
}

export const useLogin = (): UseLoginResult => {
	const { checkStatus, loading, error } = useAuthStatus();
	const message = ref<string | null>(null);

	const login = async (returnUrl?: string): Promise<boolean> => {
		message.value = null;
		try {
			const status = await checkStatus(returnUrl);
			if (status.authenticated) {
				return true;
			}
			if (!status.loginUrl) {
				message.value = LOGIN_NOT_CONFIGURED_MESSAGE;
				return false;
			}
			// Navigate the whole browser (not fetch or the router): the login happens on an external identity provider.
			window.location.assign(status.loginUrl);
		} catch (caught) {
			error.value = caught instanceof Error ? caught.message : String(caught);
		}
		return false;
	};

	return { login, message, error, loading };
};
